import { maybe, rd, RemoteData } from '@passionware/monads';
import { useCurrentTeamName } from 'v4/entities/common/hooks/useTeam';
import { VisitorVendorState } from '../../../../api/visitor-vendor-state/visitor-vendor-state.api';
import { myServices } from '../../../../services/services.connected.app';
import { getVendorStatus } from '../../vendor-state/utils/getVendorStatus';

export interface BookingTabStatus {
  overallStatus:
    | 'all-synced'
    | 'has-drift'
    | 'has-errors'
    | 'not-eligible'
    | 'no-vendors';
  vendorCounts: {
    total: number;
    synced: number;
    drifted: number;
    errors: number;
    notEligible: number;
  };
}

/**
 * Custom hook that analyzes booking vendor states and determines overall status.
 * Used for displaying status badges on the tab and in the header.
 * Fetches data using the VisitorVendorStateService.
 */
export const useBookingTabStatus = (
  bookingId: number
): RemoteData<BookingTabStatus> => {
  const teamName = useCurrentTeamName();

  // Fetch booking with vendor states
  const bookingData =
    myServices.visitorVendorStateService.useListWithBookingDetails(
      maybe.map(teamName, teamName => ({
        schema: teamName,
        filters: {
          page: 1,
          count: 1,
          booking_ids: [bookingId],
          include_current_snapshots: true
        }
      }))
    );

  return rd.useMemoMap(bookingData, bookingData => {
    const booking = bookingData.results.find(r => r.booking_id === bookingId);

    if (!booking) {
      return {
        overallStatus: 'no-vendors' as const,
        vendorCounts: {
          total: 0,
          synced: 0,
          drifted: 0,
          errors: 0,
          notEligible: 0
        }
      };
    }

    const vendorStates = Object.values(booking.vendors) as VisitorVendorState[];
    const total = vendorStates.length;

    if (total === 0) {
      return {
        overallStatus: 'no-vendors' as const,
        vendorCounts: {
          total: 0,
          synced: 0,
          drifted: 0,
          errors: 0,
          notEligible: 0
        }
      };
    }

    let synced = 0;
    let drifted = 0;
    let errors = 0;
    let notEligible = 0;

    vendorStates.forEach(vendorState => {
      if (vendorState) {
        const status = getVendorStatus(vendorState);

        // Count by status type
        if (status.type === 'preparation-error') {
          errors++;
        } else if (status.type === 'not-eligible') {
          notEligible++;
        } else if (status.type === 'drift') {
          drifted++;
        } else if (status.type === 'synced' || status.type === 'registered') {
          synced++;
        }
        // not-registered and unknown don't count in any category
      }
    });

    // Determine overall status (priority order: errors > drift > synced > not-eligible > no-vendors)
    let overallStatus: BookingTabStatus['overallStatus'];
    if (errors > 0) {
      overallStatus = 'has-errors';
    } else if (drifted > 0) {
      overallStatus = 'has-drift';
    } else if (synced > 0) {
      overallStatus = 'all-synced';
    } else if (notEligible > 0) {
      overallStatus = 'not-eligible';
    } else {
      overallStatus = 'no-vendors';
    }

    return {
      overallStatus,
      vendorCounts: { total, synced, drifted, errors, notEligible }
    };
  });
};
